import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Smartphone, Mail, RefreshCw, Scissors, Trash2, FileSearch, Loader2 } from "lucide-react";
import { api, errMsg, fdatetime, SCAN_STATUS, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge, DataTable, PageHeader } from "@/components/common";
import Modal from "@/components/Modal";

const KIND = { scan: "Scan importé", email: "Reçue par email", facturx: "Factur-X" };

function confSummary(s) {
  const f = Object.values(s.fields || {});
  if (!f.length) return null;
  const c = (x) => f.filter(x).length;
  return (
    <span className="flex items-center gap-2 text-xs">
      <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-full bg-[#2E7D5B]" />{c((x) => x.confidence >= 0.85)}</span>
      <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-full bg-[#A5894E]" />{c((x) => x.confidence >= 0.5 && x.confidence < 0.85)}</span>
      <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-full bg-[#B03A3A]" />{c((x) => !x.confidence || x.confidence < 0.5)}</span>
    </span>
  );
}

export default function ScanQueue() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [split, setSplit] = useState(null);
  const input = useRef();
  const edit = can(user, "createur");
  const load = useCallback(() => api.get("/scans").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!rows.some((r) => r.status === "lecture")) return;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [rows, load]);

  const send = async (files) => {
    if (!files.length) return;
    const big = [...files].find((f) => f.size > 20 * 1024 * 1024);
    if (big) return toast.error(`${big.name} dépasse 20 Mo`);
    setBusy(true);
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    try {
      const r = await api.post("/scans", fd);
      toast.success(`${r.data.ids.length} fichier(s) importé(s) – lecture automatique en cours`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };

  const columns = [
    { key: "filename", label: "Fichier", render: (r) => <span className="font-semibold">{r.filename}</span> },
    { key: "kind", label: "Origine", value: (r) => KIND[r.kind] || r.kind, render: (r) => <span>{KIND[r.kind]}{r.sender && <span className="block text-xs text-[#6B6B80]">{r.sender} – {r.subject}</span>}</span> },
    { key: "created_at", label: "Importé le", value: (r) => fdatetime(r.created_at) },
    { key: "status", label: "Statut", value: (r) => SCAN_STATUS[r.status]?.[0], render: (r) => (
      <span className="flex items-center gap-2"><Badge meta={SCAN_STATUS[r.status]} />{r.status === "lecture" && <Loader2 size={14} className="animate-spin text-[#A5894E]" />}</span>) },
    { key: "conf", label: "Confiance", export: false, sortable: false, render: (r) => (r.status === "erreur" ? <span className="text-xs text-[#B03A3A]">{r.error}</span> : confSummary(r)) },
    { key: "actions", label: "", export: false, sortable: false, render: (r) => (
      <span className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        {r.status === "a_verifier" && <button className="btn-primary !px-3 !py-1.5" onClick={() => nav(`/factures-a-traiter/${r.id}`)} data-testid={`scan-verify-btn-${r.id}`}><FileSearch size={14} /> Vérifier</button>}
        {edit && r.status === "erreur" && <button className="btn-ghost" onClick={() => act(() => api.post(`/scans/${r.id}/retry`), "Nouvelle lecture lancée")} title="Relancer la lecture"><RefreshCw size={15} /></button>}
        {edit && r.status !== "lecture" && <button className="btn-ghost" onClick={() => setSplit(r)} title="Découper le PDF" data-testid={`scan-split-btn-${r.id}`}><Scissors size={15} /></button>}
        {edit && <button className="btn-ghost" onClick={() => window.confirm("Écarter ce scan de la file ?") && act(() => api.delete(`/scans/${r.id}`), "Scan écarté")} title="Écarter"><Trash2 size={15} /></button>}
      </span>) },
  ];

  return (
    <div>
      <PageHeader title="Factures à traiter" subtitle="Scans et pièces reçues par email en attente de vérification humaine avant d'entrer dans le circuit.">
        {edit && <>
          <button className="btn-secondary" onClick={() => act(() => api.post("/scans/mailbox/poll"), "Boîte factures relevée")} data-testid="poll-mailbox-btn"><Mail size={16} /> Relever la boîte factures</button>
          <button className="btn-secondary" onClick={() => nav("/capture")} data-testid="mobile-capture-link"><Smartphone size={16} /> Photo mobile</button>
          <button className="btn-primary" onClick={() => input.current.click()} data-testid="import-scan-btn"><Upload size={16} /> Importer une facture scannée</button>
        </>}
      </PageHeader>
      <input ref={input} type="file" multiple hidden accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.heic,.heif" onChange={(e) => { send(e.target.files); e.target.value = ""; }} data-testid="scan-file-input" />
      {edit && (
        <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); send(e.dataTransfer.files); }}
          onClick={() => input.current.click()} data-testid="scan-dropzone"
          className={`rise mb-8 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-10 text-center transition-colors ${drag ? "border-[#A5894E] bg-[#A5894E]/10" : "border-[#E4E4EC] bg-white hover:border-[#A5894E]"}`}>
          {busy ? <Loader2 className="animate-spin text-[#A5894E]" size={32} /> : <Upload size={32} className="text-[#A5894E]" />}
          <p className="mt-3 font-bold">Glissez-déposez une ou plusieurs factures ici</p>
          <p className="mt-1 text-xs text-[#6B6B80]">PDF (multipages), JPG, PNG, TIFF, HEIC — 20 Mo max par fichier · import par lot · redressement, recadrage et contraste automatiques · détection Factur-X</p>
        </div>
      )}
      <DataTable columns={columns} rows={rows} title="Factures à traiter" testid="scans-table" onRowClick={(r) => r.status === "a_verifier" && nav(`/factures-a-traiter/${r.id}`)} />
      <Modal open={!!split} onClose={() => setSplit(null)} title="Découper le PDF" description="Indiquez les plages de pages, une facture par plage (ex. 1-2;3;4-5)." testid="split-modal">
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); const v = new FormData(e.target).get("r"); act(() => api.post(`/scans/${split.id}/split`, { ranges: v }), "PDF découpé, nouvelles factures en lecture").then(() => setSplit(null)); }}>
          <input name="r" className="inp" placeholder="1-2;3" required data-testid="split-ranges-input" />
          <div className="flex justify-end"><button className="btn-primary" data-testid="split-confirm-btn"><Scissors size={15} /> Découper</button></div>
        </form>
      </Modal>
    </div>
  );
}
